import MenuSelector from '../core/MenuSelector';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import ActionText from '../core/ActionText';
import Errored from '../core/Errored';
import { globalStyles } from '../core/style/gstheme';
import { BN_NAMESPACE, NS_GS, RANGE_MEDIATYPE_MAPPING } from '../core/utils/constants';
import { getGraphFromProject, makeCancelable, sortResources } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import FindOntologyRelatedConceptPassthroughDialog from '../vocabulary/taxonomies/FindOntologyRelatedConceptPassthroughDialog';
import AssociatedProjectInfo from './AssociatedProjectInfo';
import AssociatedPropertyDisplay from './AssociatedPropertyDisplay';
import VisiblePropertyDisplay from './VisiblePropertyDisplay';

const CLASSES_TO_INCLUDE = [
    "http://www.w3.org/2004/02/skos/core#Concept",
    "http://www.w3.org/2008/05/skos-xl#Label",
    "http://www.w3.org/2004/02/skos/core#ConceptScheme",
    "http://www.w3.org/2004/02/skos/core#Collection",
    "http://www.w3.org/2004/02/skos/core#OrderedCollection"
];


const styles = Object.assign({}, globalStyles);

class AssociatedProjectDisplay extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoadingPaths: false,
            isLoadingStatements: false,
            pathList: null,
            pathLoading: true,
            statementLoading: true,
            monitorGuids: [],
            monitorIRIs: [],
            visiblePathList: [],
            selectedForVisible: [],
            statements: props.statements ? props.statements.length : 0,
            applicableStatements: [],
            globalProps: [],
            disjointProps: {}
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.collectTemplateData();
        this.collectStatements();
    }

    // So this will only update if the resource data changes or the reification - not the templates or global properties
    // That's fine - we don't want reordering whilst a user is looking at the properties anyway
    static getDerivedStateFromProps(props, state) {
        if (props.resourceData._version !== state._version || (props.statements && props.statements.length !== state.statements)) {
            return {
                ...(props.resourceData._version !== state._version) && {
                    pathLoading: true,
                    isLoadingPaths: false,
                    _version: props.resourceData._version
                },
                ...(props.statements && props.statements.length !== state.statements) && {
                    statementLoading: true,
                    isLoadingStatements: false,
                    statements: props.statements.length
                }
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.pathLoading === true) {
            this.collectTemplateData();
        }
        if (!this.state.isErrored && this.state.statementLoading === true) {
            this.collectStatements();
        }
    }

    loadData(iris, options, graph) {
        if (!graph) graph = this.props.graph;
        this.cancellable = makeCancelable(GS.loadData(iris, options, graph));
        return this.cancellable;
    }

    loadPathData(iris, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, graph));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        this.unbindEvents();
    }

    unbindEvents() {
        this.state.monitorGuids.forEach((guid, index) => {
            const iri = this.state.monitorIRIs[index];
            GS.unbind(iri, this.props.graph, guid);
        });
    }

    handleGSevent = (event) => this.collectTemplateData();

    inferComputeTemplates = async (props) => {
        const { data } = this.props;
        const associatedProjectGraph = getGraphFromProject(data.associatedProject);
        // First we get all the superclasses for the resource. Needed for global properties and templates
        // As long as not Graphifi classes
        for (let classIRI of props.resourceClasses) {
            if (!classIRI.startsWith("https://graphifi.com")) {
                await GS.loadSuperClasses(classIRI, associatedProjectGraph);
            }
        }
        // We allow for subclassing        
        let inferredResourceClasses = [];
        if (props.includeBaseClass || props.forceBaseClass) inferredResourceClasses = [props.baseInferredClass];
        for (let classIRI of props.resourceClasses) {
            if (classIRI !== props.baseInferredClass) {
                // We should have retrieved these from above
                const scList = await GS.getSuperClasses(classIRI, associatedProjectGraph);
                inferredResourceClasses = inferredResourceClasses.concat(scList);
            }
        }
        return this.computeTemplates(props, inferredResourceClasses);
    };

    // Not everything needs inference so can call this directly
    computeTemplates = (props, inferredResourceClasses = []) => {
        const { data } = this.props;
        const doesNotExist = this.props.propertyTemplates.filter(iri => !GS.isLoaded(iri, this.props.graph));
        let templates = this.props.propertyTemplates.filter(id => !doesNotExist.includes(id)
            && (!data.associatedTemplate || data.associatedTemplate.includes(id)));
        // We have a slight difference in how concepts and labels are treated - for labels there will be a target class
        // So we include the base inferred class as well in filtering)
        let applicableTemplates = [];
        for (let template of templates) {
            const templateData = GS.data(template, this.props.graph);
            if (!props.forceBaseClass) {
                if (!templateData.shTargetClass || templateData.shTargetClass.length === 0) {
                    applicableTemplates.push(template);
                    continue;
                }
            }
            else {
                if (!templateData.shTargetClass || templateData.shTargetClass.length === 0) continue;
            }
            if (props.filterTemplatesByProperty) {
                if (templateData.targetPredicate && templateData.targetPredicate.length > 0
                    && !templateData.targetPredicate.includes(props.filterTemplatesByProperty)) {
                    continue;
                }
            }
            // Shouldn't happen as Concept, Label or component class as always there
            if (!props.resourceClasses || props.resourceClasses.length === 0) continue;
            const isApplicable = inferredResourceClasses.some(rc => templateData.shTargetClass.includes(rc));
            if (isApplicable) applicableTemplates.push(template);
        }
        return applicableTemplates;
    };

    computeHasValue(property, resourceData) {
        let isVisibleWithData = false;
        const alias = GS.getAlias(property);
        if (alias in resourceData) {
            if (Array.isArray(resourceData[alias])) {
                isVisibleWithData = resourceData[alias].length > 0;
            }
            else if (resourceData[alias] === undefined) return false;
            else {
                isVisibleWithData = true;
            }
        }
        return isVisibleWithData;
    }

    collectTemplateData() {
        if (this.state.isLoadingPaths) return;
        const { data, resourceData } = this.props;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        // We load ALL the template necessary in one API call to avoid unnecessary calls
        this.setState({ isLoadingPaths: true }, async () => {
            try {
                const associatedProjectGraph = getGraphFromProject(data.associatedProject);
                // Load global properties
                await this.loadData(data.associatedProperty, {}, associatedProjectGraph).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let disjointProps = {};
                if (data.associatedProperty) {
                    data.associatedProperty.forEach(ap => {
                        if (GS.isLoaded(ap, associatedProjectGraph)) {
                            const propertyData = GS.data(ap, associatedProjectGraph);
                            if ("propertyDisjointWith" in propertyData && propertyData.propertyDisjointWith.length > 0) {
                                // We remove inverse disjointness expresions
                                const nonInverses = [...propertyData.propertyDisjointWith].filter(d => !d.startsWith(BN_NAMESPACE));
                                disjointProps[ap] = nonInverses;
                            }
                        }
                    });
                }
                await this.loadData(this.props.propertyTemplates).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const inverseList = GS.getInverses(associatedProjectGraph);
                // Need to filter to only the associated templates for this associated project
                const applicableTemplates = await this.inferComputeTemplates(this.props);
                if (this.cancellable && this.cancellable.isCancelled()) return;
                // Now load property shapes
                let propsList = [];
                let newMonitorGuids = [];
                let newMonitorIRIs = [];
                let visiblePathList = [];
                let visibleShapesList = [];
                applicableTemplates.sort().forEach(template => {
                    if (GS.isLoaded(template, this.props.graph)) {
                        const templateData = GS.data(template, this.props.graph);
                        if ("shProperty" in templateData) {
                            propsList = propsList.concat(templateData.shProperty);
                            if (!("shGroup" in templateData) || !templateData.shGroup.includes(NS_GS + "hiddenByDefault")) {
                                templateData.shProperty.forEach(sp => {
                                    if (!visibleShapesList.includes(sp)) {
                                        visibleShapesList.push(sp);
                                    }
                                });
                            }
                        }
                        // We need to monitor for changes to templates
                        const monitorGuid = GS.bindChange(template, this.props.graph, this.handleGSevent);
                        newMonitorGuids.push(monitorGuid);
                        newMonitorIRIs.push(template);
                    }
                });
                await this.loadData(propsList).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                let pathList = [];
                let pathInfo = new Map();
                propsList.forEach(shProp => {
                    if (GS.isLoaded(shProp, this.props.graph)) {
                        const shPropData = GS.data(shProp, this.props.graph);
                        if (!pathList.includes(shPropData.shPath)
                            && (!data.associatedProperty || !data.associatedProperty.includes(shPropData.shPath))) {
                            pathList.push(shPropData.shPath);
                        }
                        if (visibleShapesList.includes(shProp)) {
                            if (!visiblePathList.includes(shPropData.shPath)) {
                                visiblePathList.push(shPropData.shPath);
                            }
                        }
                        // If any value exists then it gets displayed
                        const hasValue = this.computeHasValue(shPropData.shPath, resourceData);
                        if (hasValue) {
                            if (!visiblePathList.includes(shPropData.shPath)) {
                                visiblePathList.push(shPropData.shPath);
                            }
                        }
                        if (this.state.selectedForVisible.includes(shPropData.shPath)) {
                            if (!visiblePathList.includes(shPropData.shPath)) {
                                visiblePathList.push(shPropData.shPath);
                            }
                        }
                        const propInfo = pathInfo.has(shPropData.shPath) ? pathInfo.get(shPropData.shPath) : {};
                        if (inverseList.includes(shPropData.shPath)) {
                            propInfo.hasInverse = true;
                        }
                        if (shPropData.shMinCount) {
                            // In case multiple templates defining same prop take largest minCount
                            if ("minCount" in propInfo && propInfo.minCount !== shPropData.shMinCount) propInfo.minCountConflict = true;
                            if (!("minCount" in propInfo) || shPropData.shMinCount > propInfo.minCount) {
                                propInfo.minCount = shPropData.shMinCount;
                            }
                            if ("minCount" in propInfo) {
                                if (!visiblePathList.includes(shPropData.shPath)) {
                                    visiblePathList.push(shPropData.shPath);
                                }
                            }
                        }
                        if (shPropData.shMaxCount) {
                            // In case multiple templates defining same prop take smallest maxCount
                            if ("maxCount" in propInfo && propInfo.maxCount !== shPropData.shMaxCount) propInfo.maxCountConflict = true;
                            if (!("maxCount" in propInfo) || shPropData.shMaxCount < propInfo.maxCount) {
                                propInfo.maxCount = shPropData.shMaxCount;
                            }
                        }
                        if (shPropData.shMaxLength) {
                            // In case multiple templates defining same prop take smallest maxLength
                            if (propInfo.maxLength && propInfo.maxLength !== shPropData.maxLength) propInfo.maxLengthConflict = true;
                            if (!("maxLength" in propInfo) || shPropData.shMaxLength < propInfo.maxLength) {
                                propInfo.maxLength = shPropData.shMaxLength;
                            }
                        }
                        if ("inComponent" in shPropData && shPropData.inComponent.length > 0) {
                            if (propInfo.inComponent && propInfo.inComponent !== shPropData.inComponent[0]) propInfo.inComponentConflict = true;
                            propInfo.inComponent = shPropData.inComponent[0];
                        }
                        if ("inComponentProject" in shPropData) {
                            if (propInfo.inComponentProject && propInfo.inComponentProject !== shPropData.inComponentProject) propInfo.inComponentProjectConflict = true;
                            propInfo.inComponentProject = shPropData.inComponentProject;
                        }
                        if ("shPattern" in shPropData && shPropData.shPattern !== "") {
                            if (propInfo.pattern && propInfo.pattern !== shPropData.pattern) propInfo.patternConflict = true;
                            propInfo.pattern = shPropData.shPattern;
                        }
                        if ("shName" in shPropData && shPropData.shName && shPropData.shName[this.props.defaultLang]) {
                            const nameValue = Array.isArray(shPropData.shName[this.props.defaultLang]) ?
                                shPropData.shName[this.props.defaultLang][0]
                                :
                                shPropData.shName[this.props.defaultLang];
                            if (!propInfo.name || nameValue > propInfo.name) {
                                propInfo.name = nameValue;
                            }
                        }
                        if ("shDescription" in shPropData && shPropData.shDescription && shPropData.shDescription[this.props.defaultLang]) {
                            const descValue = Array.isArray(shPropData.shDescription[this.props.defaultLang]) ?
                                shPropData.shDescription[this.props.defaultLang][0]
                                :
                                shPropData.shDescription[this.props.defaultLang];
                            if (!propInfo.description || descValue > propInfo.description) {
                                propInfo.description = descValue;
                            }
                        }
                        if (shPropData.treatContentAs && shPropData.treatContentAs !== "") {
                            if (propInfo.treatContentAs && propInfo.treatContentAs !== shPropData.treatContentAs) propInfo.treatContentAsConflict = true;
                            propInfo.treatContentAs = shPropData.treatContentAs;
                        }
                        pathInfo.set(shPropData.shPath, propInfo);
                        // We need to monitor for changes to property shapes
                        const monitorGuid = GS.bindChange(shProp, this.props.graph, this.handleGSevent);
                        newMonitorGuids.push(monitorGuid);
                        newMonitorIRIs.push(shProp);
                    }
                });

                // Need to sort the props
                await this.loadPathData(pathList, associatedProjectGraph).promise;
                // Check for disjointness
                pathList.forEach(pl => {
                    if (GS.isLoaded(pl, associatedProjectGraph)) {
                        const propertyData = GS.data(pl, associatedProjectGraph);
                        if ("propertyDisjointWith" in propertyData && propertyData.propertyDisjointWith.length > 0) {
                            // We remove inverse disjointness expresions
                            const nonInverses = [...propertyData.propertyDisjointWith].filter(d => !d.startsWith(BN_NAMESPACE));
                            disjointProps[pl] = nonInverses;
                        }
                        // If the treatecontentas is incompatible with the range remove it
                        const hasTreatContentAs = pathInfo.has(pl) && pathInfo.get(pl).treatContentAs;
                        if (hasTreatContentAs) {
                            let isValidRange = false;
                            if ("range" in propertyData) {
                                if (RANGE_MEDIATYPE_MAPPING[propertyData.range[0]]) {
                                    isValidRange = RANGE_MEDIATYPE_MAPPING[propertyData.range[0]].some(v => hasTreatContentAs === v.value);
                                }
                            }
                            else {
                                isValidRange = false;
                            }
                            if (!isValidRange) {
                                const pi = pathInfo.get(pl);
                                delete pi.treatContentAs;
                            }
                        }
                    }
                });
                const sortedGlobals = data.associatedProperty ?
                    sortResources(data.associatedProperty, associatedProjectGraph, "label", this.props.defaultLang)
                    :
                    [];
                pathList.sort((a, b) => {
                    const aData = GS.isLoaded(a, associatedProjectGraph) ? GS.data(a, associatedProjectGraph) : null;
                    const bData = GS.isLoaded(b, associatedProjectGraph) ? GS.data(b, associatedProjectGraph) : null;
                    let labelA = aData && aData.label[this.props.defaultLang] ? aData.label[this.props.defaultLang].toString().toLowerCase() : a;
                    let labelB = bData && bData.label[this.props.defaultLang] ? bData.label[this.props.defaultLang].toString().toLowerCase() : b;
                    // Use name overrides if available
                    if (pathInfo.has(a) && pathInfo.get(a).name) labelA = pathInfo.get(a).name.toLowerCase();
                    if (pathInfo.has(b) && pathInfo.get(b).name) labelB = pathInfo.get(b).name.toLowerCase();
                    if (labelA < labelB) return -1;
                    return 1;
                });
                if (this.cancellable && !this.cancellable.isCancelled()) {
                    this.unbindEvents();
                    // We need to monitor the project association for new/remove tempalates and class/global property changes
                    const monitorGuid = GS.bindChange(data.id, this.props.graph, this.handleGSevent);
                    newMonitorGuids.push(monitorGuid);
                    newMonitorIRIs.push(data.id);
                    this.setState({
                        isLoadingPaths: false,
                        pathList,
                        visiblePathList,
                        pathInfo,
                        pathLoading: false,
                        globalProps: sortedGlobals,
                        disjointProps,
                        monitorGuids: newMonitorGuids,
                        monitorIRIs: newMonitorIRIs
                    });
                }
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) {
                    this.setState({ isErrored: e, isLoadingPaths: false });
                }
            }
        });
    }

    collectStatements = async () => {
        if (this.state.isLoadingStatements) return;
        try {
            this.setState({ isLoadingStatements: true }, async () => {
                const { statements } = this.props;
                let applicableStatements = [];
                await this.loadData(statements).promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                for (let s of statements) {
                    if (GS.isLoaded(s, this.props.graph)) {
                        const data = GS.data(s, this.props.graph);
                        applicableStatements.push(data);
                    }
                }
                this.setState({
                    applicableStatements,
                    statementLoading: false,
                    isLoadingStatments: false
                });
                return applicableStatements;
            });
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e, statementLoading: false, isLoadingStatments: false });
        }
    };

    handleSelectProperty = (property) => {
        const visiblePathList = this.state.visiblePathList;
        const selectedForVisible = this.state.selectedForVisible;
        const newVisiblePathList = [...visiblePathList, property];
        const newSelectedForVisible = [...selectedForVisible, property];
        this.setState({ visiblePathList: newVisiblePathList, selectedForVisible: newSelectedForVisible });
    };
    render() {

        const { isReadOnly, data, defaultLang, components, onDelete, onUpdate, projectLang, linkedData, reify, onReify, informEmpty,
            includeExistingResource, addNewLiteral, addNewTypedLiteral, viewLang, graph, associatedTaxonomyProject, global,
            resourceClasses, resourceData, projectIRI, schemeIRI, useWorkflow, dialogComponent, projectType, suppress } = this.props;
        const { pathList, pathInfo, isErrored, applicableStatements, disjointProps, selectedForVisible,
            globalProps, visiblePathList } = this.state;

        if (isErrored) return <Errored dataTest="apd-error" data={isErrored} />;

        const associatedProjectGraph = getGraphFromProject(data.associatedProject);
        const areGlobalProps = global && data.associatedProperty ? data.associatedProperty.length > 0 : false;

        const hiddenPaths = [{
            label: "Select a Property to Display",
            disabled: true,
            value: "default"
        }];
        pathList && pathList
            .filter(ap => !visiblePathList.includes(ap) && !selectedForVisible.includes(ap))
            .forEach(ap => {
                const info = pathInfo.get(ap);
                const isLoaded = GS.isLoaded(ap, associatedProjectGraph);
                if (isLoaded) {
                    const vpData = GS.data(ap, associatedProjectGraph);
                    // This won't catch a concurrent deprecation/deletion because a rerender isn't forced
                    // But that's fine - the display of the panel will handle that
                    if (!vpData.deprecated && !vpData.pendingDeletion) {
                        let isValidForDomain;
                        const inferredResourceClasses = [].concat.apply([], resourceClasses
                            .map(classIRI => CLASSES_TO_INCLUDE.includes(classIRI) ?
                                [classIRI]
                                :
                                GS.getSuperClasses(classIRI, associatedProjectGraph)));
                        isValidForDomain = !vpData.domain || vpData.domain.length === 0
                            || (vpData.domain.every(domainClass => domainClass.startsWith(BN_NAMESPACE)))
                            || (vpData.domain.includes("http://www.w3.org/2002/07/owl#Thing") && vpData.domain.length === 1)
                            || vpData.domain.some(domainClass => inferredResourceClasses.includes(domainClass));
                        if (isValidForDomain) {
                            hiddenPaths.push({
                                label: <VisiblePropertyDisplay
                                    info={info}
                                    data={vpData}
                                    defaultLang={defaultLang}
                                    id={ap}
                                    graph={associatedProjectGraph}
                                />,
                                value: ap
                            });
                        }
                    }
                }
            });

        return (
            <React.Fragment>
                {((data.associatedProperty && data.associatedProperty.length > 0)
                    || (pathList && pathList.length > 0)) &&
                    <Grid data-test="global-props" item xs={12}>
                        <AssociatedProjectInfo
                            graph={associatedProjectGraph}
                            id={data.associatedProject}
                            includeHeading={areGlobalProps}
                            defaultLang={defaultLang}
                            handleNotAvailable={true} />
                    </Grid>
                }
                {!global && pathList && pathList.length === 0 && informEmpty &&
                    <Grid data-test="global-props" item xs={12}>
                        <ActionText offset="120px">
                            <span>No applicable properties to display.</span>
                        </ActionText>
                    </Grid>
                }
                {areGlobalProps &&
                    globalProps.map(ap =>
                        <AssociatedPropertyDisplay
                            dialog={dialogComponent}
                            key={ap}
                            reify={reify}
                            computeTemplates={this.computeTemplates}
                            resourceData={resourceData}
                            resourceClasses={resourceClasses}
                            applicableStatements={applicableStatements}
                            onReify={onReify}
                            viewLang={viewLang}
                            useWorkflow={useWorkflow}
                            linkedData={linkedData}
                            suppress={suppress}
                            disjointProps={disjointProps}
                            projectType={projectType}
                            projectLang={projectLang}
                            projectIRI={projectIRI}
                            addNewLiteral={addNewLiteral}
                            addNewTypedLiteral={addNewTypedLiteral}
                            associatedProject={associatedTaxonomyProject}
                            includeExistingResource={includeExistingResource}
                            components={components}
                            isReadOnly={isReadOnly}
                            sourceComponent={schemeIRI}
                            onAssociatedPropertyDelete={onDelete}
                            onAssociatedPropertyUpdate={onUpdate}
                            id={ap}
                            handleNotAvailable={true}
                            graph={associatedProjectGraph}
                            projectGraph={graph}
                            associatedProjectGraph={associatedProjectGraph}
                            defaultLang={defaultLang} />
                    )
                }
                {pathList && pathList.length > 0 &&
                    <React.Fragment>
                        {global &&
                            <Grid data-test="template-props" item xs={12}>
                                <div><Typography variant="caption" style={{ marginTop: 6 }}>Template</Typography></div>
                            </Grid>
                        }
                        {hiddenPaths && hiddenPaths.length > 1 && !isReadOnly &&
                            <React.Fragment>
                                <Grid data-test="additional-template-props" item xs={12} md={12} lg={6}>
                                    <div style={{ width: '100%' }}>
                                        <Typography align="right" variant="body2">Additional Template Properties</Typography>
                                        <MenuSelector
                                            dataTest="property-selector"
                                            selected="default"
                                            defaultSelectionBehaviour={false}
                                            handleChange={this.handleSelectProperty}
                                            options={hiddenPaths} />
                                    </div>
                                </Grid>
                                <Grid data-test="props-splitter" item xs={12}></Grid>
                            </React.Fragment>
                        }
                        {pathList.map(ap =>
                            <AssociatedPropertyDisplay
                                dialog={dialogComponent}
                                key={ap}
                                reify={reify}
                                computeTemplates={this.computeTemplates}
                                applicableStatements={applicableStatements}
                                onReify={onReify}
                                info={pathInfo.get(ap)}
                                resourceData={resourceData}
                                resourceClasses={resourceClasses}
                                viewLang={viewLang}
                                useWorkflow={useWorkflow}
                                projectLang={projectLang}
                                visiblePathList={visiblePathList}
                                projectType={projectType}
                                projectIRI={projectIRI}
                                linkedData={linkedData}
                                suppress={suppress}
                                disjointProps={disjointProps}
                                associatedProject={associatedTaxonomyProject}
                                addNewLiteral={addNewLiteral}
                                addNewTypedLiteral={addNewTypedLiteral}
                                includeExistingResource={includeExistingResource}
                                components={components}
                                isReadOnly={isReadOnly}
                                sourceComponent={schemeIRI}
                                onAssociatedPropertyDelete={onDelete}
                                onAssociatedPropertyUpdate={onUpdate}
                                id={ap}
                                handleNotAvailable={true}
                                graph={associatedProjectGraph}
                                projectGraph={graph}
                                associatedProjectGraph={associatedProjectGraph}
                                defaultLang={defaultLang} />
                        )}
                    </React.Fragment>
                }
            </React.Fragment>
        );
    }
}

AssociatedProjectDisplay.defaultProps = {
    propertyTemplates: [],
    statements: [],
    suppress: [],
    useWorkflow: false,
    informEmpty: false,
    baseInferredClass: "http://www.w3.org/2004/02/skos/core#Concept",
    includeBaseClass: false,
    global: true,
    forceBaseClass: false,
    dialogComponent: FindOntologyRelatedConceptPassthroughDialog
};

AssociatedProjectDisplay.propTypes = {
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    components: PropTypes.array,
    includeBaseClass: PropTypes.bool,
    forceBaseClass: PropTypes.bool,
    isReadOnly: PropTypes.bool.isRequired,
    projectIRI: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    viewLang: PropTypes.string,
    projectType: PropTypes.string,
    filterTemplatesByProperty: PropTypes.string,
    reify: PropTypes.bool,
    onReify: PropTypes.func,
    suppress: PropTypes.array,
    global: PropTypes.bool,
    projectLang: PropTypes.array.isRequired,
    addNewLiteral: PropTypes.func.isRequired,
    propertyTemplates: PropTypes.array,
    linkedData: PropTypes.array,
    statements: PropTypes.array,
    informEmpty: PropTypes.bool,
    associatedTaxonomyProject: PropTypes.any,
    addNewTypedLiteral: PropTypes.func.isRequired,
    includeExistingResource: PropTypes.func,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    schemeIRI: PropTypes.string,
    resourceData: PropTypes.object.isRequired,
    resourceClasses: PropTypes.array.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(AssociatedProjectDisplay));